import { useState, useEffect, useCallback } from 'react';
import { FaCheck, FaTimes, FaChevronDown, FaChevronUp, FaBell, FaBellSlash, FaDownload, FaFilePdf, FaFileImage, FaTrash } from 'react-icons/fa';
import api from '../../api';

const STATUS_COLORS = {
  submitted: 'bg-amber-100 text-amber-700',
  approved:  'bg-emerald-100 text-emerald-700',
  rejected:  'bg-red-100 text-red-700',
  draft:     'bg-gray-100 text-gray-500',
};

function formatWeek(dateStr) {
  const d = new Date(dateStr);
  const end = new Date(d); end.setDate(d.getDate() + 6);
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

export default function TimesheetReview() {
  const [timesheets, setTimesheets]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filter, setFilter]           = useState('submitted');
  const [expanded, setExpanded]       = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [acting, setActing]           = useState(null);
  const [rejectModal, setRejectModal] = useState(null); // { id, notify }
  const [rejectReason, setRejectReason] = useState('');
  const [notify, setNotify]           = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null); // timesheet object

  const load = useCallback(() => {
    setLoading(true);
    api.getTimesheets()
      .then(data => setTimesheets(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const loadRows = async (id) => {
    if (expandedRows[id]) { setExpanded(expanded === id ? null : id); return; }
    try {
      const ts = await api.getTimesheet(id);
      setExpandedRows(prev => ({ ...prev, [id]: { rows: ts.rows || [], attachment: ts.attachment || null } }));
      setExpanded(id);
    } catch {}
  };

  const handleApprove = async (id) => {
    setActing(id);
    try {
      await api.approveTimesheet(id, { notify });
      load();
    } catch {}
    setActing(null);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setActing(confirmDelete.id);
    try {
      await api.deleteTimesheet(confirmDelete.id);
      setConfirmDelete(null);
      load();
    } catch {}
    setActing(null);
  };

  const handleRejectConfirm = async () => {
    if (!rejectModal) return;
    setActing(rejectModal.id);
    try {
      await api.rejectTimesheet(rejectModal.id, { notify: rejectModal.notify, reason: rejectReason });
      setRejectModal(null);
      setRejectReason('');
      load();
    } catch {}
    setActing(null);
  };

  const filtered = timesheets.filter(t => filter === 'all' || t.status === filter);

  const counts = {
    submitted: timesheets.filter(t => t.status === 'submitted').length,
    approved:  timesheets.filter(t => t.status === 'approved').length,
    rejected:  timesheets.filter(t => t.status === 'rejected').length,
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-10">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-[#0c1b4d]">Timesheet Review</h1>
          <p className="text-gray-500 text-sm mt-1">Review and approve employee timesheets.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Pending Review', count: counts.submitted, color: 'bg-amber-50 border-amber-200 text-amber-700' },
            { label: 'Approved',       count: counts.approved,  color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
            { label: 'Rejected',       count: counts.rejected,  color: 'bg-red-50 border-red-200 text-red-700' },
          ].map(s => (
            <div key={s.label} className={`rounded-xl border p-4 ${s.color}`}>
              <p className="text-2xl font-bold">{s.count}</p>
              <p className="text-xs font-medium mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Notification toggle */}
        <div className="flex items-center gap-2 mb-5">
          <button
            onClick={() => setNotify(!notify)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              notify ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-gray-50 border-gray-200 text-gray-500'
            }`}
          >
            {notify ? <FaBell size={11} /> : <FaBellSlash size={11} />}
            {notify ? 'Email notifications ON' : 'Email notifications OFF'}
          </button>
          <span className="text-xs text-gray-400">Toggle to control whether employees receive emails when approved/rejected.</span>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-5">
          {['submitted', 'approved', 'rejected', 'all'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${
                filter === f ? 'bg-[#0c1b4d] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-slate-50'
              }`}>
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
              {f !== 'all' && counts[f] > 0 && (
                <span className="ml-1.5 bg-white bg-opacity-20 text-xs px-1.5 py-0.5 rounded-full">
                  {counts[f]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <p className="text-gray-400 text-sm">Loading...</p>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
            <p className="text-gray-400 text-sm">No timesheets found.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(ts => (
              <div key={ts.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                {/* Row */}
                <div className="flex items-center gap-4 px-5 py-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-[#0c1b4d] text-sm">{ts.user_name}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[ts.status]}`}>
                        {ts.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {formatWeek(ts.week_start)} · <span className="font-medium text-gray-600">{parseFloat(ts.total_hours).toFixed(1)}h</span>
                      {ts.employee_id && <span className="ml-2">ID: {ts.employee_id}</span>}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {ts.status === 'submitted' && (
                      <>
                        <button
                          onClick={() => handleApprove(ts.id)}
                          disabled={acting === ts.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                        >
                          <FaCheck size={10} /> Approve
                        </button>
                        <button
                          onClick={() => { setRejectModal({ id: ts.id, notify }); setRejectReason(''); }}
                          disabled={acting === ts.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white text-xs font-semibold rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                        >
                          <FaTimes size={10} /> Reject
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setConfirmDelete(ts)}
                      disabled={acting === ts.id}
                      className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Delete timesheet"
                    >
                      <FaTrash size={11} />
                    </button>
                    <button
                      onClick={() => loadRows(ts.id)}
                      className="p-1.5 text-gray-400 hover:text-[#0c1b4d] transition-colors"
                    >
                      {expanded === ts.id ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                    </button>
                  </div>
                </div>

                {/* Expanded rows */}
                {expanded === ts.id && expandedRows[ts.id] && (
                  <div className="border-t border-gray-50 px-5 py-4 bg-slate-50">
                    {expandedRows[ts.id].rows.length === 0 ? (
                      <p className="text-xs text-gray-400">No project rows recorded.</p>
                    ) : (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-gray-400">
                            <th className="text-left pb-2 font-medium">Project</th>
                            {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
                              <th key={d} className="text-center pb-2 font-medium w-10">{d}</th>
                            ))}
                            <th className="text-center pb-2 font-medium w-12">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {expandedRows[ts.id].rows.map(row => {
                            const total = ['mon','tue','wed','thu','fri','sat','sun'].reduce((s,d) => s + (parseFloat(row[d]) || 0), 0);
                            return (
                              <tr key={row.id}>
                                <td className="py-1.5 pr-4 text-gray-700 font-medium">{row.project_name || row.project_display_name || '—'}</td>
                                {['mon','tue','wed','thu','fri','sat','sun'].map(d => (
                                  <td key={d} className="text-center py-1.5 text-gray-500">
                                    {parseFloat(row[d]) > 0 ? parseFloat(row[d]) : '—'}
                                  </td>
                                ))}
                                <td className="text-center py-1.5 font-semibold text-[#0c1b4d]">{total.toFixed(1)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                    {ts.global_notes && (
                      <p className="text-xs text-gray-500 mt-3 italic">Notes: {ts.global_notes}</p>
                    )}

                    {/* Client timesheet attachment */}
                    {expandedRows[ts.id].attachment ? (
                      <div className="mt-4 flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3">
                        {expandedRows[ts.id].attachment.mimetype === 'application/pdf'
                          ? <FaFilePdf size={18} className="text-red-400 flex-shrink-0" />
                          : <FaFileImage size={18} className="text-blue-400 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-700 truncate">
                            {expandedRows[ts.id].attachment.original_name}
                          </p>
                          <p className="text-xs text-gray-400">
                            Client timesheet ·{' '}
                            {expandedRows[ts.id].attachment.file_size
                              ? `${(expandedRows[ts.id].attachment.file_size / 1024).toFixed(0)} KB · `
                              : ''}
                            stored on {expandedRows[ts.id].attachment.storage_type === 'db' ? 'database' : 'disk'}
                          </p>
                        </div>
                        <a
                          href={api.attachmentUrl(ts.id)}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0c1b4d] text-white text-xs font-semibold rounded-lg hover:bg-blue-900 transition-colors flex-shrink-0"
                        >
                          <FaDownload size={10} /> Download
                        </a>
                      </div>
                    ) : (
                      <p className="mt-4 text-xs text-gray-400 italic">No client timesheet attached.</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirm Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm px-6 py-5">
            <h2 className="font-bold text-gray-800 mb-1">Delete Timesheet?</h2>
            <p className="text-sm text-gray-500 mb-1">
              <span className="font-medium text-gray-700">{confirmDelete.user_name}</span> — {formatWeek(confirmDelete.week_start)}
            </p>
            <p className="text-sm text-gray-500 mb-5">
              This will permanently delete the timesheet, all rows, and any attachments. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={handleDelete}
                className="flex-1 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 transition-colors">
                Delete
              </button>
              <button onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm rounded-xl hover:bg-slate-50 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h2 className="font-bold text-[#0c1b4d] mb-1">Reject Timesheet</h2>
            <p className="text-gray-500 text-sm mb-4">Optionally add a note explaining why it's being returned.</p>
            <textarea
              rows={3}
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="e.g. Please correct the hours logged on Wednesday..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none mb-4"
            />
            <div className="flex items-center gap-2 mb-5">
              <input type="checkbox" id="notifyReject" checked={rejectModal.notify}
                onChange={e => setRejectModal({ ...rejectModal, notify: e.target.checked })}
                className="w-4 h-4 accent-blue-700" />
              <label htmlFor="notifyReject" className="text-sm text-gray-600">Send email notification to employee</label>
            </div>
            <div className="flex gap-3">
              <button onClick={handleRejectConfirm}
                className="flex-1 py-2.5 bg-red-500 text-white text-sm font-semibold rounded-xl hover:bg-red-600 transition-colors">
                Confirm Reject
              </button>
              <button onClick={() => setRejectModal(null)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm rounded-xl hover:bg-slate-50 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
